import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import {
  AsyncState,
  BookingSummary,
  StickyActionFooter
} from '@link/share/rail-booking';

import { useStore } from '@link/emot/stores';

import styles from './confirm.module.css';

const ConfirmView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { emot } = useStore();
  const isChange = emot.orderType === 'change';
  const { bookingInfo, selection } = emot;
  const { diagram, fromStation, toStation, seatClass, seatPreference } =
    selection;
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      const { orderNo } = isChange
        ? await emot.submitChange()
        : await emot.submitBooking();
      if (isChange || orderNo) {
        navigate('../submitted', {
          replace: true,
          state: { submittedOrder: true }
        });
      }
    } catch {
      // The endpoint middleware displays the API error. Keep the selection so
      // the user can retry without returning to the seat page.
    } finally {
      setSubmitting(false);
    }
  };

  if (!diagram) {
    return (
      <AsyncState
        status="error"
        title={t('views.confirm.noTrain')}
        message={t('views.confirm.selectTrainFirst')}
        actionLabel={t('common.back')}
        onAction={() => navigate(-1)}
      />
    );
  }

  const quantities =
    bookingInfo?.units?.map(
      unit => `${unit.name ?? ''} ×${unit.quantity ?? 0}`
    ) ?? [];
  const trainType = diagram.trainMode
    ? `${diagram.trainMode}${diagram.trainName ? `（${diagram.trainName}）` : ''}`
    : undefined;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>{t('views.confirm.trainConfirm')}</h1>
        <BookingSummary
          data={{
            ticketName: t('views.confirm.ticketName'),
            departTime: diagram.departTime,
            arriveTime: diagram.arriveTime,
            duration: diagram.timeDuration,
            trainName: diagram.trainName,
            fromStation: fromStation?.name,
            toStation: toStation?.name,
            travelDate: bookingInfo?.departDate,
            quantities,
            trainType,
            seatClass: seatClass?.seatClassName || bookingInfo?.seatClassName,
            seatPreference: seatPreference?.name
          }}
          labels={{
            route: t('views.confirm.route'),
            travelDate: t('views.confirm.travelDate'),
            quantities: t('views.confirm.ticketCount'),
            trainType: t('views.confirm.trainType'),
            seatClass: t('views.confirm.seatClass'),
            seatPreference: t('views.confirm.preference')
          }}
        />
      </div>

      <StickyActionFooter
        label={
          isChange
            ? t('views.change.confirmSelect')
            : t('views.confirm.confirmSelect')
        }
        loading={submitting}
        onClick={handleConfirm}
      />
    </div>
  );
};

const Confirm = observer(ConfirmView);

export default Confirm;
